import React, { CSSProperties, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useGetModelsQuery } from '../../../generated/graphql';
import { useDownloadAttachment } from '../../../App.hooks';
import DataTableNew from '../../../components/main/DataTableNew/DataTableNew';
import RefreshButton from '../../../components/main/RefreshButton/RefreshButton';
import { ticksToDate } from '../../../Utils';
import { Link } from 'react-router-dom';
import { FileWarning, ChevronDown, ChevronLeft, ChevronRight, Check, Cog, LayoutList, X } from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  GraphQL query – includes createdTime / createdBy for reports       */
/* ------------------------------------------------------------------ */
const GET_PROJECT_REPORT_DATA = gql`
    query getProjectReportDynamicData($id: String) {
        Project(id: $id) {
            dynamicData {
                id
                name
                type
                data
                createdTime
                createdById
                createdBy {
                    id
                    name
                }
            }
        }
    }
`;

const GET_PROJECT_UNAPPROVED_CONTENT_SUMMARIES = gql`
    query getProjectUnapprovedContentSummaries($projectId: String) {
        UnapprovedContentSummaries(projectId: $projectId) {
            id
            applicationName
            modelId
            modelName
            familyName
            reporterId
            reporterName
            detectionCount
            firstSeenAt
            lastSeenAt
        }
    }
`;

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */
type ReportRow = {
    id: string;
    reportName: string;
    modelName: string;
    modelId: string | null;
    familyName: string;
    createdTime: string | null;
    createdBy: string;
    status: string;
    cloudCreditDuration: number | null;
    activityTitle: string;
    occurrenceCount?: number;
    firstSeenTime?: string | null;
    applicationName?: string;
};

type ReportsProps = {
    projectId: string;
    style?: CSSProperties | undefined;
    children?: ReactNode | undefined;
};

/* ------------------------------------------------------------------ */
/*  Report card config – add new report types here                     */
/* ------------------------------------------------------------------ */
const REPORT_CARDS: { name: string; icon: React.ElementType; color: string; bgColor: string; description: string }[] = [
    { name: 'Unapproved Content', icon: FileWarning, color: 'var(--warning)', bgColor: 'var(--warning-soft)', description: 'Content items that have not yet been approved' },
    { name: 'Design Automation', icon: Cog, color: 'var(--info)', bgColor: 'var(--info-soft)', description: 'Design automation job results and cloud credit usage' },
];

/* ------------------------------------------------------------------ */
/*  Status badge helper                                                */
/* ------------------------------------------------------------------ */
const STATUS_STYLES: Record<string, { color: string; bg: string }> = {
    'Completed Successfully':        { color: 'var(--success)', bg: 'var(--success-soft)' },
    'success':                       { color: 'var(--success)', bg: 'var(--success-soft)' },
    'Completed With Warnings':       { color: 'var(--warning)', bg: 'var(--warning-soft)' },
    'Warnings detected in logs':     { color: 'var(--warning)', bg: 'var(--warning-soft)' },
    'Completed With Errors':         { color: 'var(--danger)',  bg: 'var(--danger-soft, #fef2f2)' },
    'Errors detected in logs':       { color: 'var(--danger)',  bg: 'var(--danger-soft, #fef2f2)' },
};

const StatusBadge = ({ value }: { value: string }) => {
    const s = STATUS_STYLES[value] || { color: 'var(--text-secondary)', bg: 'var(--surface-100)' };
    return (
        <span
            className="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
            style={{ color: s.color, backgroundColor: s.bg }}
        >
            {value}
        </span>
    );
};

/* ------------------------------------------------------------------ */
/*  Design Automation column definitions + cell renderer               */
/* ------------------------------------------------------------------ */

type DAColumn = { id: string; name: string; width?: string };

const DA_COLUMNS: DAColumn[] = [
    { id: 'modelName',            name: 'Model' },
    { id: 'activityTitle',        name: 'Activity' },
    { id: 'status',               name: 'Status',               width: '220px' },
    { id: 'cloudCreditDuration',  name: 'Cloud Credits (min)',  width: '160px' },
    { id: 'createdTime',          name: 'Created',              width: '120px' },
    { id: 'createdBy',            name: 'Created By',           width: '160px' },
];

const renderDACell = (colId: string, row: ReportRow, modelId: string | null): React.ReactNode => {
    switch (colId) {
        case 'modelName':
            return modelId
                ? <Link to={`../model/${modelId}`} className="text-xs hover:underline" style={{ color: 'var(--accent-text)', textDecoration: 'none' }}>{row.modelName}</Link>
                : <>{row.modelName}</>;
        case 'status':
            return <StatusBadge value={row.status} />;
        case 'cloudCreditDuration':
            return <>{row.cloudCreditDuration != null ? `${row.cloudCreditDuration} min` : '\u2014'}</>;
        case 'createdTime':
            return <>{ticksToDate(row.createdTime)}</>;
        default:
            return <>{(row as any)[colId] ?? ''}</>;
    }
};

/* ------------------------------------------------------------------ */
/*  Reports tab component                                              */
/* ------------------------------------------------------------------ */
const Reports = ({ projectId, style }: ReportsProps) => {
    const [reportRows, setReportRows] = useState<ReportRow[]>([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [selectedReport, setSelectedReport] = useState<string | null>(null);
    const [groupByField, setGroupByField] = useState<string | null>(null);
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
    const [groupMenuOpen, setGroupMenuOpen] = useState(false);
    const groupMenuRef = useRef<HTMLDivElement>(null);
    const download = useDownloadAttachment();

    /* Fetch dynamic-data items (all types, filtered below) */
    const { data, loading, refetch } = useQuery(GET_PROJECT_REPORT_DATA, {
        variables: { id: projectId },
        skip: !projectId,
    });
    const {
        data: unapprovedSummaryData,
        loading: unapprovedSummaryLoading,
        refetch: refetchUnapprovedSummaries,
    } = useQuery(GET_PROJECT_UNAPPROVED_CONTENT_SUMMARIES, {
        variables: { projectId },
        skip: !projectId,
    });

    /* Fetch models for this project so we can link ModelName → model page */
    const modelData = useGetModelsQuery({
        variables: { projectId, contains: "" },
        skip: !projectId,
    });

    const modelLookup = useMemo(() => {
        const map = new Map<string, string>();
        for (const m of (modelData.data?.ModelList?.nodes || []) as any[]) {
            if (m?.name) map.set(m.name, m.id);
        }
        return map;
    }, [modelData.data]);

    /* Filter to only "Report" type items */
    const reports = useMemo(
        () =>
            (data?.Project?.dynamicData ?? [])
                .filter((d: any) => d?.type === 'Report')
                .filter((d: any) => (d?.name || '').trim().toLowerCase() !== 'unapproved content')
                .filter(Boolean) as {
                    id: string;
                    name: string;
                    type: string;
                    data: string;
                    createdTime: string | null;
                    createdById: string | null;
                    createdBy: { id: string; name: string } | null;
                }[],
        [data],
    );

    /* Count of report items per report name */
    const reportCounts = useMemo(() => {
        const counts = new Map<string, number>();
        for (const r of reports) {
            counts.set(r.name, (counts.get(r.name) || 0) + 1);
        }
        const unapprovedCount = (unapprovedSummaryData?.UnapprovedContentSummaries || [])
            .reduce((sum: number, row: any) => sum + Number(row?.detectionCount || 0), 0);
        counts.set('Unapproved Content', unapprovedCount);
        return counts;
    }, [reports, unapprovedSummaryData]);

    const unapprovedRows = useMemo<ReportRow[]>(() => {
        const summaries = new Map<string, ReportRow>();
        for (const row of (unapprovedSummaryData?.UnapprovedContentSummaries || [])) {
            const key = [row.applicationName, row.modelId || row.modelName, row.familyName, row.reporterId].join('|');
            const existing = summaries.get(key);
            if (existing) {
                existing.occurrenceCount = (existing.occurrenceCount || 0) + Number(row.detectionCount || 0);
                existing.firstSeenTime = String(Math.min(Number(existing.firstSeenTime || Number.MAX_SAFE_INTEGER), Number(row.firstSeenAt || Number.MAX_SAFE_INTEGER)));
                existing.createdTime = String(Math.max(Number(existing.createdTime || 0), Number(row.lastSeenAt || 0)));
                continue;
            }
            summaries.set(key, {
                id: row.id,
                reportName: 'Unapproved Content',
                applicationName: row.applicationName || '',
                modelName: row.modelName || '',
                modelId: row.modelId || null,
                familyName: row.familyName || '',
                createdTime: row.lastSeenAt ? String(row.lastSeenAt) : null,
                firstSeenTime: row.firstSeenAt ? String(row.firstSeenAt) : null,
                createdBy: row.reporterName || row.reporterId || '',
                occurrenceCount: Number(row.detectionCount || 0),
                status: '',
                cloudCreditDuration: null,
                activityTitle: '',
            });
        }
        return Array.from(summaries.values()).sort((a, b) => (b.occurrenceCount || 0) - (a.occurrenceCount || 0));
    }, [unapprovedSummaryData]);

    /* Download every report's JSON payload and flatten into table rows */
    useEffect(() => {
        if (!reports.length) {
            setReportRows([]);
            return;
        }

        let cancelled = false;
        setIsDownloading(true);

        const promises = reports.map(async (report) => {
            if (!report.data) return [];
            try {
                const raw = await download(report.data);
                let parsed: any;
                if (typeof raw === 'string') {
                    try {
                        parsed = JSON.parse(raw);
                    } catch {
                        // Blob may be base64-encoded
                        parsed = JSON.parse(atob(raw));
                    }
                } else {
                    parsed = raw;
                }
                const items: any[] = Array.isArray(parsed) ? parsed : [parsed];
                return items.map((item, idx) => ({
                    id: `${report.id}-${idx}`,
                    reportName: report.name ?? '',
                    modelName: item.ModelName ?? item.CmsModelName ?? '',
                    modelId: null as string | null,
                    familyName: item.FamilyName ?? '',
                    createdTime: report.createdTime ?? null,
                    createdBy: report.createdBy?.name || report.createdById || '',
                    status: item.Status ?? '',
                    cloudCreditDuration: item.CloudCreditDuration ?? null,
                    activityTitle: item.ActivityTitle ?? '',
                }));
            } catch {
                return [];
            }
        });

        Promise.all(promises).then((results) => {
            if (cancelled) return;
            setReportRows(results.flat() as ReportRow[]);
        }).finally(() => {
            if (!cancelled) setIsDownloading(false);
        });

        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reports]);

    /* Match ModelName → Model ID from the project's model list for linking */
    const tableData = useMemo(
        () => {
            const selectedRows = selectedReport === 'Unapproved Content' ? unapprovedRows : reportRows;
            return selectedRows
                .filter((row) => row.reportName === selectedReport)
                .map((row) => ({
                    ...row,
                    modelId: row.modelId || modelLookup.get(row.modelName) || null,
                }));
        },
        [reportRows, unapprovedRows, modelLookup, selectedReport],
    );

    /* Group Design Automation rows by the selected field */
    const groupedData = useMemo(() => {
        if (!groupByField) return null;
        const map = new Map<string, ReportRow[]>();
        for (const row of tableData) {
            const key = String((row as any)[groupByField] ?? '');
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(row);
        }
        return Array.from(map.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([value, rows]) => ({
                value,
                rows,
                totalCredits: groupByField !== 'cloudCreditDuration'
                    ? rows.reduce((sum, r) => sum + (r.cloudCreditDuration ?? 0), 0)
                    : null,
            }));
    }, [tableData, groupByField]);

    /* Close group-by menu on outside click */
    useEffect(() => {
        if (!groupMenuOpen) return;
        const handler = (e: MouseEvent) => {
            if (!groupMenuRef.current?.contains(e.target as Node)) setGroupMenuOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [groupMenuOpen]);

    /* ------------------------------------------------------------------ */
    /*  Card selection view                                                */
    /* ------------------------------------------------------------------ */
    if (!selectedReport) {
        return (
            <div className="flex flex-col h-full" style={style} data-testid="Reports">
                <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                    <RefreshButton onRefresh={() => { refetch(); refetchUnapprovedSummaries(); modelData.refetch(); }} />
                </div>
                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {REPORT_CARDS.map((card) => {
                            const Icon = card.icon;
                            const count = reportCounts.get(card.name) || 0;
                            return (
                                <div
                                    key={card.name}
                                    className="card p-5 cursor-pointer group transition-all hover:shadow-md"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setSelectedReport(card.name)}
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <div
                                            className="w-10 h-10 rounded-xl flex items-center justify-center"
                                            style={{ backgroundColor: card.bgColor }}
                                        >
                                            <Icon size={20} style={{ color: card.color }} />
                                        </div>
                                        {count > 0 && (
                                            <span
                                                className="text-xs font-semibold px-2 py-0.5 rounded-full"
                                                style={{ backgroundColor: card.bgColor, color: card.color }}
                                            >
                                                {count} {count === 1 ? 'item' : 'items'}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                                        {card.name}
                                    </div>
                                    <div className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                                        {card.description}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    /* ------------------------------------------------------------------ */
    /*  Detail table view (filtered to selected report)                    */
    /* ------------------------------------------------------------------ */
    return (
        <div className="flex flex-col h-full" style={style} data-testid="Reports">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                <button
                    className="flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-md transition-colors"
                    style={{ color: 'var(--accent-text)', background: 'none', border: 'none', cursor: 'pointer' }}
                    onClick={() => { setSelectedReport(null); setGroupByField(null); setCollapsedGroups(new Set()); }}
                >
                    <ChevronLeft size={16} />
                    Back
                </button>
                <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {selectedReport}
                </span>
                <div className="flex-1" />

                {/* Active group chip */}
                {groupByField && (
                    <div
                        className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full"
                        style={{ backgroundColor: 'var(--accent-soft)', color: 'var(--accent-text)' }}
                    >
                        <LayoutList size={11} />
                        <span>Grouped by <strong>{DA_COLUMNS.find(c => c.id === groupByField)?.name}</strong></span>
                        <button
                            onClick={() => { setGroupByField(null); setCollapsedGroups(new Set()); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex', padding: 0, marginLeft: 2 }}
                        >
                            <X size={11} />
                        </button>
                    </div>
                )}

                {/* Group by dropdown — Design Automation only */}
                {selectedReport === 'Design Automation' && (
                    <div className="relative" ref={groupMenuRef}>
                        <button
                            className="btn-secondary flex items-center gap-1.5 text-xs"
                            onClick={() => setGroupMenuOpen(v => !v)}
                        >
                            <LayoutList size={12} /> Group by
                        </button>
                        {groupMenuOpen && (
                            <div
                                className="card absolute right-0 py-1 rounded-lg"
                                style={{
                                    top: 'calc(100% + 4px)', zIndex: 20, minWidth: 180,
                                    border: '1px solid var(--border-default)',
                                    boxShadow: 'var(--shadow-md, 0 4px 12px rgba(0,0,0,.12))',
                                }}
                            >
                                {DA_COLUMNS.map(col => (
                                    <button
                                        key={col.id}
                                        className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-left"
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)' }}
                                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
                                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                        onClick={() => { setGroupByField(col.id); setCollapsedGroups(new Set()); setGroupMenuOpen(false); }}
                                    >
                                        <span style={{ width: 16, display: 'inline-flex', justifyContent: 'center', flexShrink: 0 }}>
                                            {groupByField === col.id && <Check size={11} style={{ color: 'var(--accent)' }} />}
                                        </span>
                                        <span className={groupByField === col.id ? 'font-semibold' : ''}>{col.name}</span>
                                    </button>
                                ))}
                                {groupByField && (
                                    <>
                                        <div style={{ borderTop: '1px solid var(--border-default)', margin: '4px 8px' }} />
                                        <button
                                            className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-left"
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)' }}
                                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
                                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                            onClick={() => { setGroupByField(null); setCollapsedGroups(new Set()); setGroupMenuOpen(false); }}
                                        >
                                            <span style={{ width: 16 }} />
                                            Clear grouping
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}

                <RefreshButton onRefresh={() => { refetch(); refetchUnapprovedSummaries(); modelData.refetch(); }} />
            </div>

            <div className="flex flex-col flex-1 overflow-hidden">
                {selectedReport === 'Design Automation' && groupByField && groupedData ? (
                    /* ── Grouped table ─────────────────────────────────────── */
                    <div className="flex-1 overflow-auto">
                        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.8125rem' }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                                <tr style={{ backgroundColor: 'var(--surface-1, var(--surface-100))', borderBottom: '2px solid var(--border-default)' }}>
                                    {DA_COLUMNS.map(col => (
                                        <th
                                            key={col.id}
                                            className="text-left px-3 py-2 text-xs font-semibold select-none"
                                            style={{ color: 'var(--text-secondary)', width: col.width, whiteSpace: 'nowrap' }}
                                        >
                                            {col.name}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {groupedData.map(({ value, rows, totalCredits }) => {
                                    const isCollapsed = collapsedGroups.has(value);
                                    const toggle = () => setCollapsedGroups(prev => {
                                        const next = new Set(prev);
                                        if (next.has(value)) next.delete(value); else next.add(value);
                                        return next;
                                    });
                                    return (
                                        <React.Fragment key={`grp-${value}`}>
                                            {/* Group header */}
                                            <tr
                                                style={{
                                                    backgroundColor: 'var(--surface-1, var(--surface-100))',
                                                    borderTop: '2px solid var(--border-default)',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={toggle}
                                            >
                                                <td colSpan={DA_COLUMNS.length} className="px-3 py-2">
                                                    <div className="flex items-center gap-2">
                                                        {isCollapsed
                                                            ? <ChevronRight size={13} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                                                            : <ChevronDown size={13} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />}
                                                        <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                                                            {value || '—'}
                                                        </span>
                                                        <span
                                                            className="text-xs px-1.5 py-0.5 rounded-full font-medium"
                                                            style={{ backgroundColor: 'var(--accent-soft)', color: 'var(--accent-text)' }}
                                                        >
                                                            {rows.length}
                                                        </span>
                                                        {totalCredits != null && totalCredits > 0 && (
                                                            <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                                                · {totalCredits} min credits
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                            {/* Data rows */}
                                            {!isCollapsed && rows.map(row => (
                                                <tr
                                                    key={row.id}
                                                    style={{ borderBottom: '1px solid var(--border-subtle, var(--border-default))' }}
                                                >
                                                    {DA_COLUMNS.map(col => (
                                                        <td
                                                            key={col.id}
                                                            className="px-3 py-2"
                                                            style={{ color: 'var(--text-primary)', verticalAlign: 'middle' }}
                                                        >
                                                            {renderDACell(col.id, row, row.modelId)}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    /* ── Default DataTableNew ──────────────────────────────── */
                    <DataTableNew
                        sortingMode="client"
                        isLoading={loading || isDownloading || (selectedReport === 'Unapproved Content' && unapprovedSummaryLoading)}
                        data={tableData}
                        showName={false}
                        columns={selectedReport === 'Design Automation' ? [
                            {
                                id: 'modelName',
                                name: 'Model',
                                sortable: true,
                                render: (row: any) =>
                                    row.modelId
                                        ? <Link to={`../model/${row.modelId}`} className="text-xs hover:underline" style={{ color: 'var(--accent-text)', textDecoration: 'none' }}>{row.modelName}</Link>
                                        : <>{row.modelName}</>,
                            },
                            { id: 'activityTitle', name: 'Activity', sortable: true },
                            {
                                id: 'status',
                                name: 'Status',
                                sortable: true,
                                width: '220px',
                                render: (row: any) => <StatusBadge value={row.status} />,
                            },
                            {
                                id: 'cloudCreditDuration',
                                name: 'Cloud Credits (min)',
                                sortable: true,
                                width: '160px',
                                render: (row: any) => <>{row.cloudCreditDuration != null ? `${row.cloudCreditDuration} min` : '—'}</>,
                            },
                            {
                                id: 'createdTime',
                                name: 'Created',
                                sortable: true,
                                width: '120px',
                                render: (row: any) => <>{ticksToDate(row.createdTime)}</>,
                            },
                            { id: 'createdBy', name: 'Created By', width: '160px', sortable: true },
                        ] : [
                            { id: 'applicationName', name: 'Application', sortable: true, width: '140px' },
                            {
                                id: 'modelName',
                                name: 'Model',
                                sortable: true,
                                render: (row: any) =>
                                    row.modelId
                                        ? <Link to={`../model/${row.modelId}`} className="text-xs hover:underline" style={{ color: 'var(--accent-text)', textDecoration: 'none' }}>{row.modelName}</Link>
                                        : <>{row.modelName}</>,
                            },
                            { id: 'familyName', name: 'Family Name', sortable: true },
                            {
                                id: 'occurrenceCount',
                                name: 'Occurrences',
                                sortable: true,
                                width: '120px',
                            },
                            {
                                id: 'firstSeenTime',
                                name: 'First Seen',
                                sortable: true,
                                width: '120px',
                                render: (row: any) => <>{ticksToDate(row.firstSeenTime)}</>,
                            },
                            {
                                id: 'createdTime',
                                name: 'Last Seen',
                                sortable: true,
                                width: '120px',
                                render: (row: any) => <>{ticksToDate(row.createdTime)}</>,
                            },
                            { id: 'createdBy', name: 'Reported By', width: '160px', sortable: true },
                        ]}
                    />
                )}
            </div>
        </div>
    );
};

export default Reports;
